<template>
    <v-container>
        <v-card :loading="loading">
            <v-card-title :style="{ 'background-color': '#eeeeee' }">
                <div>
                    LAMITECH PANEL PRICES<br />
                    <span
                        class="text-body-2"
                        style="text-align: left !important;"
                    >
                        Double click a price to edit
                    </span>
                </div>
                <v-spacer />
                <v-btn
                    color="primary"
                    rounded
                    @click="saveAll"
                    :loading="loading"
                    :disabled="true"
                >
                    SAVE
                </v-btn>
            </v-card-title>

            <div class="table-container">
                <v-simple-table dense fixed-header>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th rowspan="2" class="text-left">Product</th>
                                <th rowspan="2" class="text-center">
                                    Thickness (mm)
                                </th>
                                <th rowspan="2" class="text-center">
                                    Dimension
                                </th>
                                <th rowspan="2" class="text-center">Finish</th>
                                <th
                                    colspan="6"
                                    class="text-center font-weight-bold"
                                >
                                    PRICES
                                </th>
                            </tr>
                            <tr>
                                <th colspan="1" class="text-center">
                                    <!-- edit colspan when add new colors-->
                                    Solid Color
                                </th>
                                <th colspan="1" class="text-center">Printed</th>
                                <!-- edit colspan when add new colors-->
                            </tr>
                            <tr>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th class="text-center">Brown</th>
                                <!-- <th class="text-center">Black</th> -->
                                <!-- <th class="text-center">White</th> -->
                                <th class="text-center">Brown</th>
                                <!-- <th class="text-center">Black</th> -->
                                <!-- <th class="text-center">White</th> -->
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="product in tableData" :key="product.id">
                                <!-- Informational Data -->
                                <td>{{ product.product }}</td>
                                <td class="text-center">
                                    {{ product.thickness }}
                                </td>
                                <td class="text-center">
                                    {{ product.dimension }}
                                </td>
                                <td class="text-center">
                                    {{ product.finish }}
                                </td>

                                <!-- 
                                  CORRECT STRUCTURE: 
                                  The outer loop is on a <template> tag, which doesn't render an element.
                                  The inner loop is on the <td> tag, creating a new table cell for each price.
                                -->
                                <template
                                    v-for="category in ['unicolor', 'printed']"
                                >
                                    <td
                                        v-for="color in [
                                            'brown',
                                            // 'black',
                                            // 'white',
                                        ]"
                                        :key="
                                            `${product.id}-${category}-${color}`
                                        "
                                        class="text-center price-cell"
                                        @dblclick="
                                            startEditing(
                                                product.id,
                                                category,
                                                color
                                            )
                                        "
                                    >
                                        <v-text-field
                                            v-if="
                                                isEditing(
                                                    product.id,
                                                    category,
                                                    color
                                                )
                                            "
                                            v-model.number="
                                                product[category][color]
                                            "
                                            outlined
                                            dense
                                            hide-details
                                            autofocus
                                            @blur="stopEditing()"
                                            @keydown.enter="stopEditing()"
                                            style="width: 100px; margin: auto;"
                                            type="number"
                                        ></v-text-field>
                                        <span v-else>
                                            {{
                                                formatCurrency(
                                                    product[category][color]
                                                )
                                            }}
                                        </span>
                                    </td>
                                </template>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </div>
        </v-card>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
// Initial data simulating an API response

export default {
    name: 'LamitechPriceTable',
    props: {
        settingId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            tableData: [],
            editingCell: { productKey: null, category: null, color: null },
        }
    },
    async mounted() {
        try {
            await this.getItems()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // Simulates fetching data from an API
        async getItems() {
            try {
                this.loading = true
                this.tableData = await API.getLamitechPanelPrices({
                    settingId: this.settingId,
                })
                if (this.tableData.length < 1) {
                    this.tableData = this.initialLamitechPanelPrices
                }
                this.tableData.sort((a, b) => a.thickness - b.thickness)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        // Sets the active cell for editing
        startEditing(productKey, category, color) {
            this.editingCell = { productKey, category, color }
        },
        // Checks if a specific cell is the one being edited
        isEditing(productKey, category, color) {
            return (
                this.editingCell.productKey === productKey &&
                this.editingCell.category === category &&
                this.editingCell.color === color
            )
        },
        // Executes when editing of a cell stops
        async stopEditing() {
            try {
                if (!this.editingCell.productKey) return

                this.loading = true
                const { productKey, category, color } = this.editingCell
                const product = this.tableData.find(p => p.id === productKey)
                const newValue = product[category][color]

                if (product) {
                    // update the item in the API
                    await API.updateLamitechPanelPrices({
                        settingId: this.settingId,
                        id: product.id,
                        paramsToUpdate: {
                            [`${category}.${color}`]: newValue,
                        },
                    })
                }
                // reset the editing cell
                this.editingCell = {
                    row: null,
                    column: null,
                }
            } catch (error) {
                console.error('Error saving the price:', error)
            } finally {
                this.editingCell = {
                    productKey: null,
                    category: null,
                    color: null,
                }
                this.loading = false
            }
        },
        // Logic for a "Save All" button
        async saveAll() {
            try {
                this.loading = true
                await API.createLamitechPanelPrices({
                    settingId: this.settingId,
                    items: this.tableData,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        // Formats numbers as currency
        formatCurrency(value) {
            if (
                value === null ||
                typeof value === 'undefined' ||
                isNaN(value)
            ) {
                return 'N/A'
            }
            // Change 'en-US' and 'USD' to your desired locale and currency
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(value)
        },
    },
}
</script>

<style scoped>
.table-container {
    overflow-x: auto;
}
.price-cell {
    min-height: 48px;
    padding: 4px 8px;
    vertical-align: middle;
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    padding: 0 10px;
}
</style>
